import { useState, useCallback } from 'react';
import { useReports, DEFAULT_FILTERS, type ReportFiltersState } from '../../hooks/useReports';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { usePayees } from '../../hooks/usePayees';
import { generatePdfReport } from '../../lib/pdfExport';
import { PlanGate } from '../../components/feature/PlanGate';
import { ReportFilters } from './components/ReportFilters';
import { ExportPanel } from './components/ExportPanel';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { ReportSummaryCards } from './components/ReportSummaryCards';
import { PremiumInsightsPanel } from './components/PremiumInsightsPanel';
import { IncomeExpenseChart } from './components/IncomeExpenseChart';
import { CategoryBreakdown } from './components/CategoryBreakdown';
import { TopSpending } from './components/TopSpending';
import { MonthlyComparison } from './components/MonthlyComparison';
import { StatusOriginCharts } from './components/StatusOriginCharts';
import { BalanceProjection } from './components/BalanceProjection';
import { CashFlowTimeline } from './components/CashFlowTimeline';
import { FutureCommitments } from './components/FutureCommitments';
import { CategoryInsights } from './components/CategoryInsights';
import { AccountCardBreakdown } from './components/AccountCardBreakdown';
import { GoalsVsReality } from './components/GoalsVsReality';
import { AnalyticsTable } from './components/AnalyticsTable';
import { PaymentMethodBreakdown } from './components/PaymentMethodBreakdown';
import { PayeeBreakdown } from './components/PayeeBreakdown';

type TabId = 'visao-geral' | 'fluxo' | 'categorias' | 'contas-cartoes' | 'favorecidos' | 'metas' | 'tabela';

// ─── Helper: derive ISO date strings from report filter period ─────────────
function deriveReportDates(filters: ReportFiltersState): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (filters.period === 'custom' && filters.customStart && filters.customEnd) {
    return { dateFrom: filters.customStart, dateTo: filters.customEnd };
  }

  const months = filters.period === '1m' ? 1 : filters.period === '2m' ? 2 :
    filters.period === '3m' ? 3 : filters.period === '6m' ? 6 : 12;

  const from = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { dateFrom: iso(from), dateTo: iso(to) };
}

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'visao-geral',    label: 'Visão Geral',       icon: 'ri-dashboard-line' },
  { id: 'fluxo',          label: 'Fluxo de Caixa',    icon: 'ri-exchange-funds-line' },
  { id: 'categorias',     label: 'Categorias',         icon: 'ri-pie-chart-line' },
  { id: 'contas-cartoes', label: 'Contas & Cartões',   icon: 'ri-bank-line' },
  { id: 'favorecidos',    label: 'Por Favorecido',     icon: 'ri-user-3-line' },
  { id: 'metas',          label: 'Metas',              icon: 'ri-flag-line' },
  { id: 'tabela',         label: 'Tabela Analítica',   icon: 'ri-table-line' },
];

const EMPTY_DATA = {
  totalReceitas: 0, totalDespesas: 0, saldo: 0, transactionCount: 0,
  ticketMedio: 0, totalPendente: 0, pctRecorrentes: 0, pctParcelados: 0,
  topCategoria: '—', distribConta: 0, distribCartao: 0, mediaDiaria: 0,
  expensesByCategory: [], incomeByCategory: [], monthlyData: [], topExpenses: [],
  paidTotal: 0, pendingTotal: 0, overdueTotal: 0,
  insights: [], premiumInsights: [],
  prevPeriodDespesas: 0, prevPeriodReceitas: 0,
  filteredTransactions: [], categories: [],
  balanceProjection: { currentBalance: 0, expectedIncome: 0, pendingExpenses: 0, overdueAmount: 0, estimatedEndBalance: 0, riskLevel: 'safe' as const },
  cashFlow30: { income: 0, expenses: 0, net: 0, items: [] },
  cashFlow60: { income: 0, expenses: 0, net: 0, items: [] },
  cashFlow90: { income: 0, expenses: 0, net: 0, items: [] },
  accountBreakdown: [], cardBreakdown: [],
  categoryGrowth: [], goalsVsReal: [],
  payeeBreakdown: [],
};

function RelatoriosContent() {
  const [filters, setFilters] = useState<ReportFiltersState>(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState<TabId>('visao-geral');
  const [catTab, setCatTab] = useState<'despesa' | 'receita'>('despesa');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const { data, loading, isPremium, accounts, cards } = useReports(filters);
  const permissions = usePlanPermissions();
  const { settings: sysSettings } = useSystemSettings();
  const { activePayees } = usePayees();
  const rd = data ?? EMPTY_DATA;
  const { dateFrom, dateTo } = deriveReportDates(filters);

  const handleExportCSV = useCallback(() => {
    if (!data || !permissions.canExportCsv) return;
    const rows = [
      ['Data','Descrição','Favorecido','Categoria','Tipo','Origem','Status','Recorrência','Parcelamento','Valor'],
      ...data.filteredTransactions.map(t => [
        t.date, t.description, t.payee_name ?? '', t.category, t.type, t.originName, t.status,
        t.recurrence ?? '', t.installments > 1 ? `${t.installment_number}/${t.installments}` : '',
        t.amount.toFixed(2),
      ]),
      [''], ['RESUMO'],
      ['Total Receitas', data.totalReceitas.toFixed(2)],
      ['Total Despesas', data.totalDespesas.toFixed(2)],
      ['Saldo', data.saldo.toFixed(2)],
      ['Total Pendente', data.totalPendente.toFixed(2)],
    ];

    // Add payee breakdown section if available
    if (data.payeeBreakdown.length > 0) {
      rows.push([''], ['POR FAVORECIDO'], ['Favorecido', 'Total', 'Lançamentos', '% do Total']);
      data.payeeBreakdown.forEach(p => {
        rows.push([p.payee_name, p.total.toFixed(2), String(p.count), `${p.percent}%`]);
      });
    }

    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `relatorio-${filters.period}-${new Date().toLocaleDateString('pt-BR').replace(/\//g,'-')}.csv`;
    a.click();
  }, [data, filters, permissions.canExportCsv]);

  const handleExportPDF = useCallback(async () => {
    if (!permissions.canExportPdf || !data) return;
    setPdfGenerating(true);
    try {
      await generatePdfReport({ data, filters, settings: sysSettings, activeTab });
    } finally {
      setPdfGenerating(false);
    }
  }, [permissions.canExportPdf, data, filters, sysSettings, activeTab]);

  return (
    <div className="space-y-5 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Relatórios &amp; Insights</h1>
          <p className="text-sm text-gray-500 mt-0.5">Painel premium de decisão financeira com dados em tempo real</p>
        </div>
        <ExportPanel
          data={rd}
          isPremium={isPremium}
          canExportCsv={permissions.canExportCsv}
          canExportPdf={permissions.canExportPdf}
          onExportCSV={handleExportCSV}
          onExportPDF={handleExportPDF}
          pdfGenerating={pdfGenerating}
        />
      </div>

      {/* Filters */}
      <ReportFilters
        filters={filters}
        onChange={setFilters}
        isPremium={isPremium}
        categories={rd.categories}
        accounts={accounts}
        cards={cards}
        payees={activePayees}
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-600 transition-all cursor-pointer whitespace-nowrap flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${tab.icon} text-[13px]`} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* — ABA: Visão Geral — */}
      {activeTab === 'visao-geral' && (
        <div className="space-y-5">
          <ExecutiveSummary data={rd} loading={loading} />
          <PremiumInsightsPanel insights={rd.premiumInsights} loading={loading} />
          <ReportSummaryCards data={rd} loading={loading} />

          <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
            <div className="mb-4">
              <h3 className="text-[15px] font-700 text-gray-900">Receitas x Despesas</h3>
              <p className="text-[12px] text-gray-400 mt-0.5">Evolução mensal no período selecionado</p>
            </div>
            {loading ? <div className="h-56 bg-gray-50 rounded-xl animate-pulse" /> : (
              <IncomeExpenseChart monthlyData={rd.monthlyData} />
            )}
          </div>

          <StatusOriginCharts data={rd} loading={loading} />

          <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="text-[15px] font-700 text-gray-900">Comparativo Mensal</h3>
              <p className="text-[12px] text-gray-400 mt-0.5">Evolução de receitas, despesas e saldo mês a mês</p>
            </div>
            {loading ? <div className="h-40 bg-gray-50 rounded-xl animate-pulse" /> : (
              rd.monthlyData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                  <i className="ri-table-line text-3xl text-gray-200" />
                  <p className="text-sm text-gray-400">Sem dados suficientes para comparação</p>
                </div>
              ) : <MonthlyComparison monthlyData={rd.monthlyData} />
            )}
          </div>
        </div>
      )}

      {/* — ABA: Fluxo de Caixa — */}
      {activeTab === 'fluxo' && (
        <div className="space-y-5">
          <BalanceProjection projection={rd.balanceProjection} loading={loading} />
          <CashFlowTimeline cf30={rd.cashFlow30} cf60={rd.cashFlow60} cf90={rd.cashFlow90} loading={loading} />
          <FutureCommitments cf90={rd.cashFlow90} overdueTotal={rd.overdueTotal} loading={loading} />
        </div>
      )}

      {/* — ABA: Categorias — */}
      {activeTab === 'categorias' && (
        <div className="space-y-5">
          <CategoryInsights categoryGrowth={rd.categoryGrowth} expensesByCategory={rd.expensesByCategory} loading={loading} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[15px] font-700 text-gray-900">Por Categoria</h3>
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                  {(['despesa','receita'] as const).map(t => (
                    <button key={t} onClick={() => setCatTab(t)}
                      className={`px-3 py-1 text-[11px] font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                        catTab === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
                      }`}>
                      {t === 'despesa' ? 'Despesas' : 'Receitas'}
                    </button>
                  ))}
                </div>
              </div>
              {loading ? <div className="h-40 bg-gray-50 rounded-xl animate-pulse" /> : (
                <CategoryBreakdown
                  data={catTab === 'despesa' ? rd.expensesByCategory : rd.incomeByCategory}
                  type={catTab}
                />
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
              <h3 className="text-[15px] font-700 text-gray-900 mb-4">Maiores Despesas</h3>
              {loading ? (
                <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-gray-50 rounded-xl animate-pulse" />)}</div>
              ) : <TopSpending expenses={rd.topExpenses} isPremium={isPremium} />}
            </div>
          </div>
        </div>
      )}

      {/* — ABA: Contas & Cartões — */}
      {activeTab === 'contas-cartoes' && (
        <div className="space-y-5">
          <AccountCardBreakdown accounts={rd.accountBreakdown} cards={rd.cardBreakdown} loading={loading} />
          <PaymentMethodBreakdown dateFrom={dateFrom} dateTo={dateTo} />
          <StatusOriginCharts data={rd} loading={loading} />
        </div>
      )}

      {/* — ABA: Por Favorecido — */}
      {activeTab === 'favorecidos' && (
        <div className="space-y-5">
          <PayeeBreakdown payeeBreakdown={rd.payeeBreakdown} loading={loading} />
        </div>
      )}

      {/* — ABA: Metas — */}
      {activeTab === 'metas' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="mb-5">
            <h3 className="text-[15px] font-700 text-gray-900">Metas vs. Realizado</h3>
            <p className="text-[12px] text-gray-400 mt-0.5">Progresso das suas metas ativas e risco de não atingi-las</p>
          </div>
          <GoalsVsReality goals={rd.goalsVsReal} loading={loading} />
        </div>
      )}

      {/* — ABA: Tabela — */}
      {activeTab === 'tabela' && (
        <AnalyticsTable transactions={rd.filteredTransactions} onExportCSV={handleExportCSV} />
      )}
    </div>
  );
}

export default function RelatoriosPage() {
  return (
    <PlanGate
      feature="canAccessReports"
      mode="page"
      title="Relatórios & Insights"
      description="Visualize análises completas das suas finanças, gráficos de evolução, comparativos mensais e insights automáticos."
    >
      <RelatoriosContent />
    </PlanGate>
  );
}
